/**
 * 📦 COMPOSANT : sellerie-app/frontend/src\pages\Clients\NouveauClient.tsx
 * Ce composant est utilisé dans d'autres pages de l'application
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { clientService, Client } from '../../services/ClientService';

const NouveauClient: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  
  // Vérifier si on est redirigé depuis le configurateur d'article
  const returnToDevis = location.state?.returnToDevis || false;
  const articlesDevis = location.state?.articlesDevis || [];
  
  const [loading, setLoading] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [client, setClient] = useState<Partial<Client>>({
    nom: '',
    prenom: '',
    societe: '',
    email: '',
    telephone: '',
    adresse: '',
    codePostal: '',
    ville: '',
    pays: 'France',
    notes: '',
  });

  // Charger les données du client si on est en mode édition
  useEffect(() => {
    const fetchClient = async () => {
      if (id) {
        try {
          setLoading(true);
          setIsEditMode(true);
          
          // Récupérer le client depuis le service
          const clientData = await clientService.getClient(id);
          setClient(clientData);
          
          setLoading(false);
        } catch (error) {
          console.error('Erreur lors du chargement du client:', error);
          alert('Client non trouvé');
          navigate('/clients');
        }
      }
    };
    
    fetchClient();
  }, [id, navigate]);

  // Gérer les changements dans le formulaire
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setClient((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Gérer la soumission du formulaire
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation basique
    if (!client.nom || !client.prenom || !client.email || !client.telephone) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    try {
      setLoading(true);
      
      // Sauvegarder le client via le service
      const savedClient = await clientService.saveClient(client as Client);
      
      // Si on est redirigé depuis le configurateur d'article, retourner au configurateur avec le client créé
      if (returnToDevis) {
        navigate('/devis/nouveau', { 
          state: { 
            clientId: savedClient.id,
            articlesDevis: articlesDevis
          } 
        });
      } else {
        // Sinon, rediriger vers la page de détails du client
        navigate(`/clients/${savedClient.id}`);
      }
    } catch (error) {
      console.error(`Erreur lors de la ${isEditMode ? 'modification' : 'création'} du client:`, error);
      alert(`Une erreur est survenue lors de la ${isEditMode ? 'modification' : 'création'} du client`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-900">
          {isEditMode ? 'Modifier le client' : 'Nouveau client'}
        </h1>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => navigate('/clients')}
        >
          Annuler
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Informations générales</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="societe" className="block text-sm font-medium text-gray-700 mb-1">
                Société
              </label>
              <input
                type="text"
                id="societe"
                name="societe"
                className="input-field w-full"
                value={client.societe}
                onChange={handleChange}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="nom" className="block text-sm font-medium text-gray-700 mb-1">
                  Nom *
                </label>
                <input
                  type="text"
                  id="nom"
                  name="nom"
                  className="input-field w-full"
                  value={client.nom}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <label htmlFor="prenom" className="block text-sm font-medium text-gray-700 mb-1">
                  Prénom *
                </label>
                <input
                  type="text"
                  id="prenom"
                  name="prenom"
                  className="input-field w-full"
                  value={client.prenom}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                className="input-field w-full"
                value={client.email}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <label htmlFor="telephone" className="block text-sm font-medium text-gray-700 mb-1">
                Téléphone *
              </label>
              <input
                type="tel"
                id="telephone"
                name="telephone"
                className="input-field w-full"
                value={client.telephone}
                onChange={handleChange}
                required
              />
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Adresse</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label htmlFor="adresse" className="block text-sm font-medium text-gray-700 mb-1">
                Adresse
              </label>
              <input
                type="text"
                id="adresse"
                name="adresse"
                className="input-field w-full"
                value={client.adresse}
                onChange={handleChange}
              />
            </div>
            <div>
              <label htmlFor="codePostal" className="block text-sm font-medium text-gray-700 mb-1">
                Code postal
              </label>
              <input
                type="text"
                id="codePostal"
                name="codePostal"
                className="input-field w-full"
                value={client.codePostal}
                onChange={handleChange}
              />
            </div>
            <div>
              <label htmlFor="ville" className="block text-sm font-medium text-gray-700 mb-1">
                Ville
              </label>
              <input
                type="text"
                id="ville"
                name="ville"
                className="input-field w-full"
                value={client.ville}
                onChange={handleChange}
              />
            </div>
            <div>
              <label htmlFor="pays" className="block text-sm font-medium text-gray-700 mb-1">
                Pays
              </label>
              <input
                type="text"
                id="pays"
                name="pays"
                className="input-field w-full"
                value={client.pays}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Notes</h2>
          <div>
            <textarea
              id="notes"
              name="notes"
              rows={4}
              className="input-field w-full"
              value={client.notes}
              onChange={handleChange}
              placeholder="Ajouter des notes sur ce client..."
            ></textarea>
          </div>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate('/clients')}
          >
            Annuler
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={loading}
          >
            {loading 
              ? (isEditMode ? 'Modification en cours...' : 'Création en cours...') 
              : (isEditMode ? 'Enregistrer les modifications' : 'Créer le client')
            }
          </button>
        </div>
      </form>
    </div>
  );
};

export default NouveauClient;
